import React, { useState, useEffect, useCallback } from 'react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import maestroIcon from '../assets/icons/Maestro-Favicon.png';
import { loadStripeSafe, resetStripeLoaderCache, STRIPE_BROWSER_FALLBACK_URL } from '../utils/stripeLoader';

function trialEndDate() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const DARK = '#1a1a2e';
const GOLD = '#C9A84C';

function StripeLoadErrorPanel({ message, onRetry }) {
  return (
    <div style={{ padding: '32px 24px', textAlign: 'center' }}>
      <p style={{ fontSize: 15, fontWeight: 700, color: '#c0392b', margin: '0 0 10px' }}>
        {message || 'Failed to load Stripe.js'}
      </p>
      <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: '0 0 20px' }}>
        Trouble loading the payment form? Open{' '}
        <a href={STRIPE_BROWSER_FALLBACK_URL} style={{ color: GOLD, fontWeight: 600 }}>
          maestrodesktop.com
        </a>
        {' '}in your browser to subscribe.
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: '12px 24px', borderRadius: 12, border: 'none',
          background: GOLD, color: DARK, fontSize: 14, fontWeight: 700, cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  );
}

export default function StripeCheckoutSlideout({
  open,
  onClose,
  clientSecret,
  showOnboarding = false,
  isResubscribe = false,
  showPlanPicker = false,
  cadence = 'annual',
  onCadenceChange,
  billing,
  onConfirmPlan,
  checkoutLoading = false,
}) {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoadError, setStripeLoadError] = useState(null);
  const [stripeRetryKey, setStripeRetryKey] = useState(0);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!clientSecret || !shouldRender) return;
    let cancelled = false;
    setStripeLoadError(null);
    setStripePromise(null);

    (async () => {
      const stripe = await loadStripeSafe(stripeRetryKey > 0);
      if (cancelled) return;
      if (stripe) {
        setStripePromise(Promise.resolve(stripe));
        setStripeLoadError(null);
      } else {
        setStripeLoadError('Failed to load Stripe.js');
      }
    })();

    return () => { cancelled = true; };
  }, [clientSecret, shouldRender, stripeRetryKey]);

  const handleStripeRetry = useCallback(() => {
    resetStripeLoaderCache();
    setStripeRetryKey((k) => k + 1);
  }, []);

  const handleOverlayClick = useCallback(() => onClose?.(), [onClose]);

  if (!shouldRender) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 199999 }}>
      <div
        onClick={handleOverlayClick}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      <div
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: 480,
          background: '#ffffff',
          borderRadius: '20px 0 0 20px',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.35)',
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #C9A84C, #E8C94A, #C9A84C)',
            padding: '18px 20px', paddingTop: 'max(18px, env(safe-area-inset-top, 18px))',
            display: 'flex', alignItems: 'center', gap: 12,
            position: 'relative', flexShrink: 0,
          }}
        >
          <img src={maestroIcon} alt="" style={{ width: 36, height: 36, borderRadius: 8 }} />
          <span style={{ fontWeight: 800, fontSize: 16, color: '#1a1a2e', letterSpacing: '-0.01em' }}>
            {isResubscribe ? 'Subscribe to Maestro' : 'Start Your Free Trial'}
          </span>
          <button
            onClick={() => onClose?.()}
            style={{
              position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(26,26,46,0.12)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(26,26,46,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(26,26,46,0.12)'}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#1a1a2e" strokeWidth="2.2" strokeLinecap="round">
              <line x1="1" y1="1" x2="13" y2="13" /><line x1="13" y1="1" x2="1" y2="13" />
            </svg>
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {showOnboarding && !isResubscribe && (
            <div style={{
              padding: '24px 24px 0',
              borderBottom: '1px solid #f0f0f0',
              paddingBottom: 20,
            }}>
              <h2 style={{
                fontSize: 20, fontWeight: 800, color: '#1a1a2e',
                margin: '0 0 8px', letterSpacing: '-0.02em',
              }}>
                Welcome to Maestro
              </h2>
              <p style={{
                fontSize: 14, lineHeight: 1.6, color: '#666', margin: 0,
              }}>
                Start your 30-day free trial today. Your trial ends on{' '}
                <strong style={{ color: '#333' }}>{trialEndDate()}</strong>.
                {' '}Maestro does not automatically charge your card — subscribe to continue after your trial.
              </p>
            </div>
          )}

          {!isResubscribe && (
            <div style={{
              background: '#FFF8E7',
              padding: '14px 20px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              borderBottom: '1px solid #f0e8d0',
              flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#7a6a3a', margin: 0 }}>
                Your card will not be charged today. Your trial ends on <strong style={{ color: '#5a4d2a' }}>{trialEndDate()}</strong>. Maestro does not automatically charge you — subscribe to continue after your trial.
              </p>
            </div>
          )}

          {isResubscribe && !showPlanPicker && (
            <div style={{
              background: '#FFF8E7',
              padding: '14px 20px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              borderBottom: '1px solid #f0e8d0',
              flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
              <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#7a6a3a', margin: 0 }}>
                Welcome back! Your personal music valet remembers you. Subscribe and pick up right where you left off.
              </p>
            </div>
          )}

          {showPlanPicker && !clientSecret && billing ? (
            <div style={{ padding: '24px 20px 28px' }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: DARK, margin: '0 0 6px', letterSpacing: '-0.02em' }}>
                Choose your plan
              </h2>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: '#666', margin: '0 0 20px' }}>
                Billing starts today. Cancel anytime from your account settings.
              </p>
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                {[
                  {
                    id: 'monthly',
                    label: 'Monthly',
                    price: `${billing.symbol}${billing.monthly}/mo`,
                    detail: 'Billed monthly',
                  },
                  {
                    id: 'annual',
                    label: 'Annual',
                    price: `${billing.symbol}${billing.annual}/yr`,
                    detail: `≈ ${billing.symbol}${billing.annualMonthlyEquivalent}/mo`,
                    badge: 'Best value',
                  },
                ].map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => onCadenceChange?.(plan.id)}
                    style={{
                      flex: 1, padding: '16px 12px', borderRadius: 14, cursor: 'pointer',
                      border: cadence === plan.id ? `2px solid ${GOLD}` : '2px solid #e5e5e5',
                      background: cadence === plan.id ? `${GOLD}08` : 'white',
                      textAlign: 'center', position: 'relative',
                    }}
                  >
                    {plan.badge && cadence === plan.id && (
                      <span style={{
                        position: 'absolute', top: -10, right: 12,
                        background: GOLD, color: DARK, fontSize: 10, fontWeight: 800,
                        padding: '2px 8px', borderRadius: 6,
                      }}>{plan.badge}</span>
                    )}
                    <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>{plan.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: DARK, margin: '4px 0 2px' }}>{plan.price}</div>
                    {plan.detail && <div style={{ fontSize: 12, color: '#888' }}>{plan.detail}</div>}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={onConfirmPlan}
                disabled={checkoutLoading}
                style={{
                  width: '100%', padding: '16px 0', borderRadius: 14, border: 'none',
                  background: `linear-gradient(135deg, ${GOLD}, #E8C94A, ${GOLD})`,
                  color: DARK, fontSize: 16, fontWeight: 800,
                  cursor: checkoutLoading ? 'wait' : 'pointer',
                  opacity: checkoutLoading ? 0.7 : 1,
                  boxShadow: `0 6px 28px ${GOLD}40`,
                }}
              >
                {checkoutLoading ? 'Opening checkout…' : 'Continue to checkout'}
              </button>
              <p style={{ fontSize: 12, color: '#bbb', marginTop: 16, textAlign: 'center', lineHeight: 1.5 }}>
                Maestro requires a separate Qobuz subscription for streaming (not included).
              </p>
            </div>
          ) : stripeLoadError && clientSecret ? (
            <StripeLoadErrorPanel message={stripeLoadError} onRetry={handleStripeRetry} />
          ) : clientSecret && stripePromise ? (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : clientSecret ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: showOnboarding ? 200 : '100%' }}>
              <div style={{ width: 32, height: 32, border: '3px solid #e5e5e5', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: showOnboarding ? 200 : '100%' }}>
              <div style={{ width: 32, height: 32, border: '3px solid #e5e5e5', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
